import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import ConfirmDialog from '../components/ConfirmDialog'
import useCopyToClipboard from '../hooks/useCopyToClipboard'
import { useTagList } from '../graphql/tagQueries'
import { CREATE_TAG, UPDATE_TAG, DELETE_TAG } from '../graphql/tagMutations'
import TagCriteriaEditor from '../tabs/TagsTab/TagCriteriaEditor'
import ManualTagging from '../tabs/TagsTab/ManualTagging'
import TaggedPatientsPanel from '../tabs/TagsTab/TaggedPatientsPanel'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const TagDetails = () => {
  const { tagId } = useParams()
  const navigate = useNavigate()
  const isEditing = !!tagId

  const [formData, setFormData] = useState({
    name: '',
    tagType: 'outreach',
    description: '',
    startDate: '',
    endDate: '',
    active: true,
  })
  const [error, setError] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Inline editing state
  const [editingName, setEditingName] = useState(false)
  const [tempName, setTempName] = useState('')
  const [editingType, setEditingType] = useState(false)
  const [editingDescription, setEditingDescription] = useState(false)
  const { copied, copy: handleCopyTagId } = useCopyToClipboard(tagId)

  const { loading: loadingTags, data: tagsData } = useTagList()
  const tags = useMemo(() => tagsData?.tags || [], [tagsData])

  const tagData = useMemo(() => {
    return tags.find((t) => t.id === tagId)
  }, [tags, tagId])

  // Get unique tag types from existing tags for autocomplete
  const existingTypes = useMemo(() => {
    const types = new Set(tags.map((t) => t.tagType))
    return Array.from(types).map((t) => ({ value: t, label: t }))
  }, [tags])

  useEffect(() => {
    if (tagData && isEditing) {
      setFormData({
        name: tagData.tag || '',
        tagType: tagData.tagType || 'outreach',
        description: tagData.description || '',
        startDate: tagData.startDate || '',
        endDate: tagData.endDate || '',
        active: tagData.active ?? true,
      })
    }
  }, [tagData, isEditing])

  const [createTag, { loading: creating }] = useMutation(CREATE_TAG, {
    refetchQueries: ['ListTags'],
    onCompleted: (data) => {
      const newTagId = data?.createTag?.tag?.id
      if (newTagId) {
        navigate(`${ROUTE_METADATA.adminOutreachTags.path}/${newTagId}`)
      } else {
        navigate(ROUTE_METADATA.adminOutreachTags.path)
      }
    },
    onError: () => setError('Failed to create tag'),
  })

  const [updateTag] = useMutation(UPDATE_TAG, {
    refetchQueries: ['ListTags'],
    onError: () => setError('Failed to update tag'),
  })

  const [deleteTag, { loading: deleting }] = useMutation(DELETE_TAG, {
    refetchQueries: ['ListTags'],
    onCompleted: () => navigate(ROUTE_METADATA.adminOutreachTags.path),
    onError: () => setError('Failed to delete tag'),
  })

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const autoSave = useCallback(
    async (field, value) => {
      if (!isEditing) return
      setError(null)
      try {
        await updateTag({
          variables: {
            input: {
              tagId,
              tag: field === 'name' ? value : formData.name,
              tagType: field === 'tagType' ? value : formData.tagType,
              description:
                field === 'description' ? value : formData.description,
              startDate:
                field === 'startDate'
                  ? value || null
                  : formData.startDate || null,
              endDate:
                field === 'endDate' ? value || null : formData.endDate || null,
              active: field === 'active' ? value : formData.active,
            },
          },
        })
      } catch {
        setError('Failed to update tag')
      }
    },
    [isEditing, tagId, formData, updateTag],
  )

  const handleNameEdit = useCallback(() => {
    setTempName(formData.name)
    setEditingName(true)
  }, [formData.name])

  const handleNameSave = useCallback(async () => {
    if (tempName.trim()) {
      setFormData((prev) => ({ ...prev, name: tempName }))
      await autoSave('name', tempName)
      setEditingName(false)
    }
  }, [tempName, autoSave])

  const handleNameCancel = useCallback(() => {
    setTempName('')
    setEditingName(false)
  }, [])

  const handleTypeChange = useCallback(
    (newValue) => {
      if (newValue) {
        setFormData((prev) => ({ ...prev, tagType: newValue }))
        autoSave('tagType', newValue)
        setEditingType(false)
      }
    },
    [autoSave],
  )

  const handleDescriptionSave = useCallback(() => {
    autoSave('description', formData.description)
    setEditingDescription(false)
  }, [formData.description, autoSave])

  const handleDescriptionCancel = useCallback(() => {
    // Revert to original description from tagData
    if (tagData) {
      setFormData((prev) => ({
        ...prev,
        description: tagData.description || '',
      }))
    }
    setEditingDescription(false)
  }, [tagData])

  const handleSave = useCallback(async () => {
    setError(null)
    const input = {
      tag: formData.name,
      tagType: formData.tagType,
      description: formData.description || null,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      active: formData.active,
    }

    if (isEditing) {
      await updateTag({ variables: { input: { tagId, ...input } } })
    } else {
      await createTag({ variables: { input } })
    }
  }, [formData, isEditing, tagId, createTag, updateTag])

  const handleDelete = useCallback(async () => {
    setError(null)
    await deleteTag({ variables: { input: { tagId } } })
    setDeleteDialogOpen(false)
  }, [tagId, deleteTag])

  if (loadingTags && isEditing) {
    return <Loading />
  }

  if (isEditing && !tagData && !loadingTags) {
    return <Alert severity="error">Tag not found</Alert>
  }

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          {isEditing ? 'Tag Details' : 'New Tag'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTE_METADATA.adminOutreachTags.path)}
          >
            Back to Tags
          </Button>
          {isEditing && (
            <>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                sx={{ '& .MuiList-root': { py: 1 } }}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null)
                    setDeleteDialogOpen(true)
                  }}
                  sx={{ px: 3, py: 1.5, minWidth: 250, color: 'error.main' }}
                >
                  Delete Tag
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </AdminPageHeader>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isEditing && tagData ? (
        <>
          {/* Editable Header for existing tag */}
          <Card
            elevation={0}
            sx={{ mb: 3, backgroundColor: 'transparent', px: 3, py: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 4,
              }}
            >
              {/* Left side: Name, Type, Description */}
              <Box sx={{ flex: 1 }}>
                {/* Editable Name with Type and Active on same line */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 0.5,
                  }}
                >
                  {editingName ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <MuiTextField
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        size="small"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleNameSave()
                          if (e.key === 'Escape') handleNameCancel()
                        }}
                        sx={{ maxWidth: 500 }}
                        variant="standard"
                        InputProps={{
                          sx: { fontSize: '2.125rem', fontWeight: 400 },
                        }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleNameSave}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton size="small" onClick={handleNameCancel}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography
                      variant="h4"
                      component="h2"
                      onClick={handleNameEdit}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.7,
                        },
                      }}
                    >
                      {formData.name}
                    </Typography>
                  )}

                  {/* Type */}
                  {editingType ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Autocomplete
                        freeSolo
                        disableClearable
                        value={formData.tagType}
                        onChange={(e, newValue) => {
                          if (newValue) {
                            setFormData((prev) => ({
                              ...prev,
                              tagType: newValue,
                            }))
                          }
                        }}
                        onInputChange={(e, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            tagType: newValue,
                          }))
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleTypeChange(formData.tagType)
                          }
                          if (e.key === 'Escape') {
                            if (tagData) {
                              setFormData((prev) => ({
                                ...prev,
                                tagType: tagData.tagType,
                              }))
                            }
                            setEditingType(false)
                          }
                        }}
                        options={existingTypes.map((t) => t.value)}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            size="small"
                            autoFocus
                            placeholder="Enter type"
                          />
                        )}
                        sx={{ width: 150 }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleTypeChange(formData.tagType)}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (tagData) {
                            setFormData((prev) => ({
                              ...prev,
                              tagType: tagData.tagType,
                            }))
                          }
                          setEditingType(false)
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => setEditingType(true)}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 16,
                        display: 'inline-block',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {formData.tagType}
                      </Typography>
                    </Box>
                  )}

                  {/* Active Toggle */}
                  <Box
                    onClick={() => {
                      const newValue = !formData.active
                      setFormData((prev) => ({ ...prev, active: newValue }))
                      autoSave('active', newValue)
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 16,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: formData.active
                          ? 'success.main'
                          : 'error.main',
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formData.active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </Box>

                {/* Tag ID */}
                <Typography variant="caption" color="text.secondary">
                  ID
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                    '& .copy-id-icon': { opacity: 0 },
                    '&:hover .copy-id-icon': { opacity: 1 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {tagId}
                  </Typography>
                  <IconButton
                    className="copy-id-icon"
                    size="small"
                    onClick={handleCopyTagId}
                    sx={{ p: 0, transition: 'opacity 0.15s' }}
                    title="Copy tag ID"
                    aria-label="Copy tag ID"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  {copied && (
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{ ml: 0.5 }}
                    >
                      Copied!
                    </Typography>
                  )}
                </Box>

                {/* Editable Description */}
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {editingDescription ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <MuiTextField
                        value={formData.description}
                        onChange={(e) => {
                          const newValue = e.target.value
                          setFormData((prev) => ({
                            ...prev,
                            description: newValue,
                          }))
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.metaKey) {
                            handleDescriptionSave()
                          }
                          if (e.key === 'Escape') {
                            handleDescriptionCancel()
                          }
                        }}
                        placeholder="Add a description..."
                        multiline
                        rows={2}
                        fullWidth
                        autoFocus
                        size="small"
                        sx={{ maxWidth: 600 }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleDescriptionSave}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleDescriptionCancel}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      onClick={() => setEditingDescription(true)}
                      sx={{
                        cursor: 'pointer',
                        fontStyle: formData.description ? 'normal' : 'italic',
                        '&:hover': {
                          opacity: 0.7,
                        },
                      }}
                    >
                      {formData.description || 'Add a description...'}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Right side: Dates */}
              <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
                <MuiTextField
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => {
                    const newValue = e.target.value
                    setFormData((prev) => ({ ...prev, startDate: newValue }))
                    autoSave('startDate', newValue)
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: 160 }}
                />
                <MuiTextField
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => {
                    const newValue = e.target.value
                    setFormData((prev) => ({ ...prev, endDate: newValue }))
                    autoSave('endDate', newValue)
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: 160 }}
                />
              </Box>
            </Box>
          </Card>

          {/* Selection Criteria and Manual Tagging */}
          <Card elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Selection Criteria
              </Typography>
              <TagCriteriaEditor tag={tagData} />
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Manual Tagging
              </Typography>
              <ManualTagging tag={tagData} />
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <TaggedPatientsPanel tag={tagData} />
            </CardContent>
          </Card>
        </>
      ) : (
        /* Simple form for new tag creation */
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Tag Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <MuiTextField
                    label="Name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    fullWidth
                    size="small"
                  />
                  <Autocomplete
                    freeSolo
                    disableClearable
                    value={formData.tagType}
                    onChange={(e, newValue) => {
                      if (newValue) handleChange('tagType', newValue)
                    }}
                    onInputChange={(e, newValue) => {
                      handleChange('tagType', newValue)
                    }}
                    options={existingTypes.map((t) => t.value)}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        size="small"
                        label="Type"
                        placeholder="Select or enter type"
                      />
                    )}
                  />
                  <MuiTextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                  />
                  <MuiTextField
                    label="Start Date"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                  <MuiTextField
                    label="End Date"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.active}
                        onChange={(e) =>
                          handleChange('active', e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </Box>
              </CardContent>
            </Card>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                type="secondary"
                onClick={() => navigate(ROUTE_METADATA.adminOutreachTags.path)}
                disabled={creating}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!formData.name || creating}
                fullWidth
              >
                {creating ? 'Creating...' : 'Create Tag'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Tag"
        message="Are you sure you want to delete this tag? This will permanently delete the tag and all associated data (patient tags, criteria, campaigns)."
        confirmLabel="Delete"
        loading={deleting}
      />
    </>
  )
}

export default TagDetails
